import { Box, Card, CardContent, Typography, Button, Grid, Dialog, IconButton, CircularProgress, Chip, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import LogTheme, { useDarkMode } from './LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders, getImageDisplayUrl } from '../../utils/api';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';

function ShowLog() {
    const { userId } = useAuth();
    const [log, setLog] = useState(null);
    const { log_id } = useParams();
    const [authorizationError, setAuthorizationError] = useState(false);
    const [authorizationErrorMessage, setAuthorizationErrorMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const navigate = useNavigate();
    const { darkMode } = useDarkMode();
    console.log('log_id ', log_id);
    useEffect(() => {
        if (!userId) {
            setLoading(false);
            // Om userId inte är tillgängligt, vänta på nästa render för att göra fetch-anropet
            navigate('/info');
            return;
        }

        fetch(`${process.env.REACT_APP_SERVER_URL}/api/showlog/${log_id}`, {
            headers: getAuthHeaders()
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 401) {
                        console.error('401: Inte autentiserad. Logga in igen.');
                        navigate('/info');
                        return;
                    }
                    if (response.status === 403) {
                        return response.json().catch(() => ({})).then((data) => {
                            setAuthorizationErrorMessage(data.message || 'Du har inte behörighet att se denna logg.');
                            setAuthorizationError(true);
                        });
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (!data) return; // Om response inte var ok, data är undefined
                console.log('Response data:', data);
                setLog(data);
                // Servern har redan godkänt åtkomst (ägare eller inbjuden med accessRole); behåll inte authorizationError
            })
            .catch(error => {
                console.error('Error:', error);
                setAuthorizationError(true);
            })
            .finally(() => setLoading(false));
    }, [log_id, userId]);

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextImage = () => {
        if (log && log.images) {
            setLightboxIndex((prev) => (prev + 1) % log.images.length);
        }
    };

    const prevImage = () => {
        if (log && log.images) {
            setLightboxIndex((prev) => (prev - 1 + log.images.length) % log.images.length);
        }
    };

    if (loading) {
        return (
            <LogTheme>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </LogTheme>
        );
    }

    if (authorizationError) {
        return (
            <LogTheme>
                <Box padding={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="error" gutterBottom>
                                {authorizationErrorMessage || 'Du har inte behörighet att se denna logg.'}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                Loggbokens ägare kan uppgradera till Premium under Prenumerationsplaner för att se alla loggar.
                            </Typography>
                            <Button component={Link} to="/home" variant="contained" sx={{ mt: 1, mr: 1 }}>
                                Tillbaka
                            </Button>
                            <Button component={Link} to="/subscription" variant="outlined" sx={{ mt: 1 }}>
                                Prenumerationsplaner
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </LogTheme>
        );
    }

    if (!log) {
        return (
            <LogTheme>
                <Box padding={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="error">
                                Loggen kunde inte hämtas.
                            </Typography>
                            <Button component={Link} to="/home" variant="contained" sx={{ mt: 2 }}>
                                Tillbaka
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </LogTheme>
        );
    }

    return (
        <LogTheme>
            <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} className="no-print">
                            <Button component={Link} to="/home" variant="outlined" startIcon={<NavigateBeforeIcon />}>
                                Tillbaka
                            </Button>
                            <Button
                                component={Link}
                                to={`/updatelog/${log._id}`}
                                variant="contained"
                                color="primary"
                                startIcon={<EditIcon />}
                            >
                                Ändra
                            </Button>
                        </Box>

                        <Box mb={3}>
                            {log.year && (
                                <Chip label={log.year} color="primary" sx={{ mb: 2, fontSize: '1rem', height: '32px' }} />
                            )}
                            <Typography variant="h1" component="h1" gutterBottom>
                                {log.title}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Avgångstid
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.departureDateTime ? new Date(log.departureDateTime).toLocaleString('sv-SE') : '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Ankomsttid
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.arrivalDateTime ? new Date(log.arrivalDateTime).toLocaleString('sv-SE') : '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Vindriktning
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.windDirection || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Vindstyrka (m/s)
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.windSpeed || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Distans (nm)
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.distance || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Från – Till
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {log.position || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                            {log.description && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Beskrivning
                                        </Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {log.description}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>

                {log.images && log.images.length > 0 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h3" gutterBottom sx={{ mb: 3 }}>
                                Bilder ({log.images.length})
                            </Typography>
                            <Grid container spacing={2}>
                                {log.images.map((imageUrl, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                        boxShadow: 4,
                                                    },
                                                }}
                                                onClick={() => openLightbox(index)}
                                            >
                                                <Box
                                                    component="img"
                                                    src={getImageDisplayUrl(imageUrl)}
                                                    alt={`Bild ${index + 1}`}
                                                    sx={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Card>
                                        </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Lightbox Dialog */}
                <Dialog
                    open={lightboxOpen}
                    onClose={closeLightbox}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.95)',
                            color: 'white',
                        },
                    }}
                >
                    <Box sx={{ position: 'relative', p: 2 }}>
                        <IconButton
                            onClick={closeLightbox}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'white',
                                zIndex: 1,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {log.images && log.images.length > 1 && (
                            <>
                                <IconButton
                                    onClick={prevImage}
                                    sx={{
                                        position: 'absolute',
                                        left: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'white',
                                        zIndex: 1,
                                    }}
                                >
                                    <NavigateBeforeIcon fontSize="large" />
                                </IconButton>
                                <IconButton
                                    onClick={nextImage}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'white',
                                        zIndex: 1,
                                    }}
                                >
                                    <NavigateNextIcon fontSize="large" />
                                </IconButton>
                            </>
                        )}
                        {log.images && log.images[lightboxIndex] && (
                            <Box
                                component="img"
                                src={getImageDisplayUrl(log.images[lightboxIndex])}
                                alt={`Bild ${lightboxIndex + 1}`}
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '80vh',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        )}
                        {log.images && log.images.length > 1 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    position: 'absolute',
                                    bottom: 16,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    color: 'white',
                                }}
                            >
                                {lightboxIndex + 1} / {log.images.length}
                            </Typography>
                        )}
                    </Box>
                </Dialog>
            </Box>
        </LogTheme>
    );
}

export default ShowLog;